<template>
  <component
    :is="component"
    :class="classes"
    :to="to"
    :target="target"
    @click="handleClick"
  >
    <NuxtIcon
      v-if="hasPattern"
      class="common-button__pattern"
      :name="patternName"
    />

    <span class="common-button__content">
      <slot v-if="!isLoading" />
      <LoaderIcon
        v-else
        class="common-button__loader anim-spin"
      />
    </span>
  </component>
</template>

<script lang="ts" setup>
import { openLink } from '@tma.js/sdk-vue';
import useIsTma from '@frontend/layer-telegram/app/composables/useIsTma';
import { NuxtLink } from '#components';
import { sleep } from '@frontend/special-utils';
import LoaderIcon from '~/components/icons/LoaderIcon.vue';

interface Props {
  theme?: 'primary' | 'secondary' | 'outlined' | 'bordered';
  size?: 'default' | 'small' | 'large' | 'custom';
  to?: string | null;
  disabled?: boolean;
  isLoading?: boolean;
}

const {
  theme = 'primary',
  size = 'default',
  to,
  disabled = false,
  isLoading = false,
} = defineProps<Props>();

const { isTma } = useIsTma();
const isPressed = ref(false);
const hasPattern = computed(() => theme === 'primary');
const patternName = computed(() => {
  return size === 'small' ? 'buttons/main-small' : size === 'large' ? 'buttons/main-large' : 'buttons/main';
});

const classes = computed(() => [
  'common-button',
  `common-button--theme-${theme}`,
  `common-button--size-${size}`,
  {
    'common-button--disabled': disabled,
    'common-button--pressed': isPressed.value,
  },
]);

const component = computed(() => {
  if (isTma.value || !to) {
    return 'button';
  }

  return to ? NuxtLink : 'button';
});

const target = computed(() => {
  const isExternal = to?.startsWith('http');
  return isExternal ? '_blank' : '_self';
});

const emit = defineEmits<{
  click: [];
}>();

const handleClick = async () => {
  if (disabled) {
    return;
  }

  isPressed.value = true;
  await sleep(150);
  isPressed.value = false;

  emit('click');

  if (isTma.value && to) {
    openLink(to);
  }
};
</script>

<style lang="scss" scoped>
.common-button {
  position: relative;
  display: flex;
  width: 22rem;
  height: 5.0rem;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  background-color: transparent;
  text-align: center;
  font-size: 2rem;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
  line-height: 115%; /* 2.3rem */
  letter-spacing: -0.08rem;
  border-radius: 0.8rem;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: transform 0.2s ease-in;

  // @include hover-opacity;

  &--theme-outlined {
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: auto;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
    text-transform: initial;
    font-size: 1.6rem;
    font-weight: 500;
    color: rgba($color-gray, 0.6);
  }

  &--theme-primary {
    background: transparent;
    color: $color-blue;
    transition: transform 0.2s ease-in, color 1s ease;

    @include hover {
      color: $color-blue-hover;
    }

    .common-button__content {
      color: $color-white;
    }
  }

  &--theme-secondary {
    background: $color-white;
    color: $color-black-deep;
    border: unset;
    transition: transform 0.2s ease-in, background 1s ease;

    @include hover {
      background: $color-white-hover;
    }
  }

  &--size-small {
    width: 13.3rem;
    height: 4.4rem;

    font-size: 1.6rem;
    font-weight: 500;

    .common-button__pattern {
      :deep(svg) {
        width: 100%;
        height: 100%;
      }
    }
  }

  &--size-large {
    width: 31.4rem;
    height: 5.0rem;

    font-size: 2rem;

    .common-button__pattern {
      :deep(svg) {
        // width: auto;
        // height: auto;
      }
    }
  }

  &--pressed {
    transform: scale(0.95);
  }

  &--disabled {
    border: unset;
    pointer-events: none;
    opacity: 0.5;
  }

  &__loader {
    width: 2.4rem;
    height: 2.4rem;

    :deep(svg) {
      width: 100%;
      height: 100%;
      display: block;
      flex: 0 0 auto;
    }
  }

  &__pattern {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: $z-base;
    color: inherit;
    transition: color 1s ease;
  }

  &__content {
    position: relative;
    z-index: $z-base;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
